import { useEffect, useState } from "react";
import { dashboardAPI } from "../../services/api";
import { useRouter } from "../../contexts/RouterContext";
import { useAuth } from "../../contexts/AuthContext";
import { Loading } from "../../components/ui";
import { fmt, StatusBadge } from "../../utils/helpers";

const COMPANY_STATUS = {
  1: { label: "Demo Mode", color: "var(--orange)", bg: "rgba(255,152,0,0.12)" },
  2: { label: "Demo Expired", color: "var(--red)", bg: "rgba(244,67,54,0.12)" },
  3: { label: "Activated", color: "var(--green)", bg: "rgba(76,175,122,0.12)" },
  4: { label: "Activation Expired", color: "var(--red)", bg: "rgba(244,67,54,0.12)" },
};

export default function Dashboard() {
  const { navigate } = useRouter();
  const { company } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const companyStatus =
    COMPANY_STATUS[Number(company?.CM_Status)] || {
      label: "Status Unknown",
      color: "var(--text-muted)",
      bg: "var(--surface2)",
    };

  useEffect(() => {
    dashboardAPI.stats().then(setStats);
    dashboardAPI.recentOrders().then(setOrders);
  }, []);

  const CARDS = stats
    ? [
        {
          title: "Total Revenue",
          value: `₹${stats.totalRevenue || 0}`,
          sub: "Lifetime revenue",
        },
        {
          title: "Active Orders",
          value: stats.totalOrders,
          sub: `${stats.pendingOrders} pending`,
        },
        {
          title: "Customers",
          value: stats.totalCustomers,
          sub: "Registered clients",
        },
        {
          title: "Karigars",
          value: stats.totalKarigars,
          sub: "Active craftsmen",
        },
      ]
    : [];

  return (
    <div className="page" style={{ padding: 24 }}>

      {/* HEADER */}
      <div className="page-header" style={{ alignItems: "center" }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">
            Overview of your tailor shop operations.
          </p>
        </div>

        <div
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            background: companyStatus.bg,
            color: companyStatus.color,
            fontSize: 13,
            fontWeight: 700,
            border: "1px solid var(--border)",
            whiteSpace: "nowrap",
          }}
        >
          {companyStatus.label}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginBottom: 18,
        }}
      >
          <div
            style={{
              display: "flex",
              gap: 10,
            }}
          >
            <button
              className="btn btn-primary"
              onClick={() => navigate("order-create")}
            >
              New Order
            </button>

            <button
              className="btn btn-ghost"
              onClick={() => navigate("customer-add")}
            >
              Add Customer
            </button>
          </div>
      </div>

      {/* CARDS */}
      {!stats ? (
        <Loading />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
            marginBottom: 24,
          }}
        >
          {CARDS.map((c, i) => (
            <div key={i} className="card" style={{ padding: 18 }}>
              <div style={{ fontSize: 20, color: "var(--text-muted)" }}>
                {c.title}
              </div>

              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  marginTop: 6,
                }}
              >
                {c.value}
              </div>

              <div
                style={{
                  fontSize: 18,
                  color: "var(--text-dim)",
                  marginTop: 4,
                }}
              >
                {c.sub}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MAIN GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 20,
        }}
      >
        {/* RECENT ORDERS */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Orders</h3>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {orders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-title">No orders yet</div>
              </div>
            ) : (
              orders.map((o) => (
                <div
                  key={o.id}
                  onClick={() => navigate("order-detail", { id: o.id })}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: 12,
                    borderBottom: "1px solid var(--border)",
                    cursor: "pointer",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 , fontSize : 18}}>
                      ORD-{o.id} — {o.customerName}
                    </div>

                    <div
                      style={{
                        fontSize: 16,
                        color: "var(--text-muted)",
                        marginTop: 4,
                      }}
                    >
                      {fmt(o.orderDate)} • ₹{o.amount || 0}
                    </div>
                  </div>

                  <StatusBadge status={o.status} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* STATUS PANEL */}
        <div className="card">
        <div className="card-header">
          <h3 className="card-title">Order Status Breakdown</h3>
        </div>

        {/* Graph */}
        <div
          style={{
            marginTop: 20,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {[
            { label: "Pending", value: stats?.pendingOrders || 0, color: "var(--orange)" },
            { label: "In Progress", value: stats?.inProgressOrders || 0, color: "var(--blue)" },
            { label: "Completed", value: stats?.completedOrders || 0, color: "var(--green)" },
          ].map((item, i) => {
            const max = Math.max(
              stats?.pendingOrders || 0,
              stats?.inProgressOrders || 0,
              stats?.completedOrders || 0,
              1
            );

            const width = (item.value / max) * 100;

            return (
              <div key={i}>
                {/* Label row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  <span>{item.label}</span>
                  <span>{item.value}</span>
                </div>

                {/* Bar */}
                <div
                  style={{
                    height: 8,
                    background: "var(--surface3)",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${width}%`,
                      height: "100%",
                      background: item.color,
                      borderRadius: 6,
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
            </div>
          </div>
  );
}

